import { QueryClient, useQuery, UseQueryResult } from '@tanstack/react-query';
import {
    Response,
    NatureResultData,
} from 'routes/HumanResourcesModule/business/types';
import { DETAILED_ENVELOPE_MANTY_STATUS_HYPOTHESES_API_KEY } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/MantyStatusHypothesis/helpers/hooks/mantyStatusHypothesis';
import { ResultTotalData } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/components/KeysIndicators/helpers/types';

import { RQAPICallWithData } from 'store/APIClient';

const RESULT_SIMULATION_DATA_API_KEY = 'result_simulation_data';

export const invalidateDetailedEnvelopeMantyStatusHypothesesQueries = (
    queryClient: QueryClient,
    hrSimulationId: number,
) => {
    queryClient.invalidateQueries({
        queryKey: [
            DETAILED_ENVELOPE_MANTY_STATUS_HYPOTHESES_API_KEY,
            hrSimulationId,
        ],
    });
};

export const resetResultSimulationDataQueries = (
    queryClient: QueryClient,
    hrSimulationId: number,
) => {
    queryClient.resetQueries({
        queryKey: [RESULT_SIMULATION_DATA_API_KEY, hrSimulationId],
    });
};

const useGetResultSimulationData = (
    simulationId: number,
    ventilation: 'monthly' | 'yearly' | 'nature' | 'service_and_nature',
) => {
    return useQuery({
        queryKey: [RESULT_SIMULATION_DATA_API_KEY, simulationId, ventilation],

        queryFn: async () => {
            let ventilationParam: string = ventilation;
            if (ventilation === 'monthly') {
                ventilationParam = 'month';
            } else if (ventilation === 'yearly') {
                ventilationParam = 'year';
            }
            const response = await RQAPICallWithData<
                ResultTotalData | Response<NatureResultData>
            >({
                endpoint: `human_resources/hr_simulation/${simulationId}/result/simulation_data/${ventilationParam}`,
            });
            return response.content.data;
        },
    });
};

/**
 * Same as `useGetResultSimulationData` but only for time-based ventilation.
 * @returns Always return a `ResultTotalData`
 */
export const useGetResultSimulationDataTimeVentilation = (
    simulationId: number,
    ventilation: 'monthly' | 'yearly',
): UseQueryResult<ResultTotalData> =>
    useGetResultSimulationData(
        simulationId,
        ventilation,
    ) as UseQueryResult<ResultTotalData>;

/**
 * Same as `useGetResultSimulationData` but only for nature-based ventilation.
 * @returns Always return a `Response<NatureResultData>`
 */
export const useGetResultSimulationDataNatureVentilation = (
    simulationId: number,
    ventilation: 'nature' | 'service_and_nature',
): UseQueryResult<Response<NatureResultData>> =>
    useGetResultSimulationData(simulationId, ventilation) as UseQueryResult<
        Response<NatureResultData>
    >;
